import React, { memo, useEffect } from "react";
import styles from "./styles.module.scss";
import GettingStartedProgress from "app/components/GettingStarted/GettingStartedProgress";
import PagePanel from "app/components/PagePanel";
import { cls } from "app/utils";
import { useActions, useSelector } from "app/utils/hooks";
import DropdownButton from "app/components/DropdownButton";
import { Link } from "react-router-dom";
import { RootState } from "typedefs";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import { fetchStudentProfile } from "app/actions/scheduling/studentProfile";
import { isBilledByOrganization } from "app/utils/subscription";
// import acountGroupIcon from "../../../public/images/account-group.svg";

interface Props {
  children: React.ReactNode;
}

interface MenuTypes {
  label: string;
  icon: string;
  url: string;
  role: string[];
  permission?: boolean;
}

// Menu entries that belong to an athlete-side account. Parent/fan accounts come
// through as kind "other" (and un-onboarded accounts fall back to it via
// menuKind), but they are athlete-side users everywhere else in the app —
// Sessions, MyBookings, the public calendar and the sidebar all test
// `athlete || other`. Family management and purchase history are theirs too: a
// parent IS the family-account holder, and they pay for their kids' programs.
// CN-2627.
const ATHLETE_SIDE_ROLES = ["athlete", "other"];

const AccountWrap = ({ children }: Props) => {
  const { t } = useTranslation();
  const { self: user } = useSelector(
    (state: RootState) => state.user,
    shallowEqual,
  );

  const studentProfile = useSelector(
    (state: RootState) => state.studentProfile,
  );
  const studentProfileActions = useActions({ fetchStudentProfile });

  // Users whose kind is still "Unknown" (e.g. Golf Genius accounts that haven't
  // been onboarded to a type) get the same settings menu as a contributor,
  // which maps to the "other" role.
  const menuKind = !user?.kind || user.kind === "Unknown" ? "other" : user.kind;
  const isAthleteSide = ATHLETE_SIDE_ROLES.includes(menuKind);

  // Athlete-side users must keep access to their purchase history regardless of
  // whether the coach who sold to them still exists. Gate the Payments &
  // Subscriptions menu on whether they have any purchases (has_purchases from
  // the scheduling student profile), not on a live Academy-plan coach
  // connection. Fetched for parent/fan ("other") accounts too — they pay for
  // their kids' programs, so withholding the fetch would keep the menu hidden
  // no matter what role the entry lists. CN-2627.
  useEffect(() => {
    if (isAthleteSide && user?.id) {
      studentProfileActions.fetchStudentProfile(user.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAthleteSide, user?.id]);

  const hasPurchases = Boolean(studentProfile?.data?.hasPurchases);

  const accountSettingsRoute: MenuTypes[] = [
    {
      label: t("Profile"),
      icon: "tab-icon ico ico-tspace",
      url: "/account/edit",
      role: ["coach", "athlete", "other"],
    },
    {
      label: t("Coach Profile"),
      // todo: fix icon
      icon: "tab-icon ico ico-grouped-people-outline",
      url: "/account/coach-profile",
      role: ["coach"],
    },
    {
      label: t("Payments & Subscriptions"),
      icon: "tab-icon ico ico-dollar-circle",
      url: "/account/athlete_subscriptions",
      role: ATHLETE_SIDE_ROLES,
      permission: hasPurchases,
    },
    {
      label: t("Password"),
      icon: "tab-icon ico ico-lock-o",
      url: "/account/password",
      role: ["coach", "athlete", "other"],
    },
    {
      label: t("Notifications"),
      icon: "tab-icon ico ico-notifications-o",
      url: "/account/settings",
      role: ["coach", "athlete", "other"],
    },
    {
      label: t("Tags"),
      icon: "tab-icon ico ico-tags-o",
      url: "/account/tags",
      role: ["coach", "athlete", "other"],
    },
    {
      label: t("Billing"),
      icon: "tab-icon ico ico-dollar",
      url: "/account/billing_info",
      role: ["coach", "athlete", "other"],
      // A coach on a licensed seat billed outside his own account holds no
      // billing info, but the page is where he learns who does pay for him —
      // the panel there is the answer.
      permission: isBilledByOrganization(user) ? true : user?.hasBillingInfo,
    },
    {
      label: t("Subscriptions"),
      icon: "tab-icon ico ico-coach",
      url: "/account/subscriptions",
      role: ["coach", "athlete", "other"],
    },
    {
      label: t("Family"),
      icon: "tab-icon ico ico-grouped-people-outline",
      url: "/account/family",
      role: ATHLETE_SIDE_ROLES,
    },
  ];

  const location = window.location.pathname;

  if (!user) {
    return null;
  }

  const renderDropdownMenu = () => {
    return (
      <DropdownButton
        btnContent={
          <i className="ico ico-cog btn btn-outline-secondary dropdown-toggle mobile-only-large" />
        }
        dropdownClassname={cls(styles.dropdown_btn, "mobile-only-large")}
        hideButtonStyles
        closeOnClick
      >
        <div className={styles.mobile_menu_list_container}>
          {accountSettingsRoute.map((menu: MenuTypes) => {
            if (
              !menu.role.includes(menuKind) ||
              (menu.permission !== undefined && !menu.permission)
            ) {
              return null;
            }
            return (
              <Link
                key={menu.url}
                className={styles.mobile_menu_list}
                to={menu.url}
              >
                <p>{menu.label}</p>
              </Link>
            );
          })}
        </div>
      </DropdownButton>
    );
  };

  return (
    <div className={cls(styles.container, "account-settings-container")}>
      <GettingStartedProgress />
      <PagePanel title={t("Settings")} headerChildren={renderDropdownMenu()} />

      <div className={styles.content_wrapper}>
        <div className={styles.left_nav_container}>
          <div className={cls(styles.nav_container, "desktop-only-large")}>
            {accountSettingsRoute.map((menu: MenuTypes) => {
              if (
                !menu.role.includes(menuKind) ||
                (menu.permission !== undefined && !menu.permission)
              ) {
                return null;
              }
              return (
                <div
                  key={menu.url}
                  className={cls(styles.nav_item, {
                    [styles.active]: location === menu.url,
                  })}
                >
                  <Link className={cls("f-middle", styles.link)} to={menu.url}>
                    <i className={cls(styles.menu_icon, menu.icon)}></i>
                    {menu.label}
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
        <div className={styles.content_container}>{children}</div>
      </div>
    </div>
  );
};

export default memo(AccountWrap);
